/**
 * useCtaInteraction - Premium CTA Micro-interaction System
 * 
 * Uses TOUCH EVENTS directly for more reliable mobile tracking.
 * Touch events don't get cancelled as easily as pointer events.
 */

import { useEffect, useRef } from "react";

// Configuration - PREMIUM SUBTLE FEEL
const CONFIG = {
  pressScale: 0.98,        // Subtle press
  maxFollow: 2,            // Max 2px follow (very subtle)
  followDamping: 0.06,     // Minimal follow
  transitionOut: "transform 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

export function useCtaInteractionSystem() {
  const stateRef = useRef({
    activeCta: null,
    startX: 0,
    startY: 0,
    touchId: null,
  });
  
  const rafIdRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof document === "undefined") return;

    // Check reduced motion
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const state = stateRef.current;

    /**
     * Apply transform
     */
    const applyTransform = (translateX = 0, translateY = 0) => {
      if (!state.activeCta) return;
      
      state.activeCta.style.transition = "none";
      
      if (translateX === 0 && translateY === 0) {
        state.activeCta.style.transform = `scale(${CONFIG.pressScale})`;
      } else {
        state.activeCta.style.transform = `scale(${CONFIG.pressScale}) translate(${translateX}px, ${translateY}px)`;
      }
    };

    /**
     * Reset CTA
     */
    const resetCta = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      
      if (state.activeCta) {
        state.activeCta.style.transition = CONFIG.transitionOut;
        state.activeCta.style.transform = "";
      }
      
      state.activeCta = null;
      state.touchId = null;
    };

    /**
     * Find touch by ID in TouchList
     */
    const findTouch = (touches, id) => {
      for (let i = 0; i < touches.length; i++) {
        if (touches[i].identifier === id) {
          return touches[i];
        }
      }
      return null;
    };

    /**
     * TOUCH START
     */
    const handleTouchStart = (e) => {
      // Only handle first touch
      if (state.activeCta) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      // Find CTA at touch point
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!target || typeof target.closest !== "function") return;
      
      const cta = target.closest("[data-cta]");
      if (!cta) return;

      // Start tracking
      state.activeCta = cta;
      state.touchId = touch.identifier;
      state.startX = touch.clientX;
      state.startY = touch.clientY;

      // Immediate press feedback
      cta.style.transition = "transform 50ms ease-out";
      cta.style.transform = `scale(${CONFIG.pressScale})`;
    };

    /**
     * TOUCH MOVE - Magnetic follow (immediate, no RAF for snappy feel)
     */
    const handleTouchMove = (e) => {
      if (!state.activeCta) return;
      if (state.touchId === null) return;

      const touch = findTouch(e.touches, state.touchId);
      if (!touch) {
        resetCta();
        return;
      }

      // Apply transform immediately (no RAF = no lag)
      const deltaX = touch.clientX - state.startX;
      const deltaY = touch.clientY - state.startY;

      const followX = clamp(deltaX * CONFIG.followDamping, -CONFIG.maxFollow, CONFIG.maxFollow);
      const followY = clamp(deltaY * CONFIG.followDamping, -CONFIG.maxFollow, CONFIG.maxFollow);

      applyTransform(followX, followY);
    };

    /**
     * TOUCH END
     */
    const handleTouchEnd = (e) => {
      if (!state.activeCta) return;
      if (state.touchId === null) return;

      // Check if our touch ended
      const touch = findTouch(e.changedTouches, state.touchId);
      if (touch) {
        resetCta();
      }
    };

    /**
     * TOUCH CANCEL
     */
    const handleTouchCancel = () => {
      resetCta();
    };

    // Attach touch listeners (NOT passive so we could preventDefault if needed)
    document.addEventListener("touchstart", handleTouchStart, { passive: true });
    document.addEventListener("touchmove", handleTouchMove, { passive: true });
    document.addEventListener("touchend", handleTouchEnd, { passive: true });
    document.addEventListener("touchcancel", handleTouchCancel, { passive: true });

    // Also handle mouse for desktop testing
    const handleMouseDown = (e) => {
      // Skip - let CSS handle desktop hover
    };

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("touchcancel", handleTouchCancel);
      
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);
}

export default useCtaInteractionSystem;
